import { Button } from "@/components/ui/button";

import { Download, Upload, Power, PowerOff, Bell, CalendarIcon, Clock, Plus, X, Loader2, Trash2, Phone } from "lucide-react";

import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { FileUploadDialog } from "@/components/file-upload-dialog";
import { useBulkCreateWarmups } from "@/hooks/use-mailbox";
import { usePermission } from "@/hooks/use-premission";
import { extractErrorMessage, validEmailSchema } from "@/lib/utils";
import { AddSenderDialog } from "@/pages/mailbox/add-sender-dialog";
import { saveAs } from "file-saver";
import { toast } from "sonner";
import { useServices } from "@/services/hooks/useServices";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useFieldArray, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useCreateSenderReminders, useGetSenderReminders, useDeleteSenderReminders } from "@/hooks/use-b2c-sender";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const SenderProfileMainActions = () => {
  const [showImportDialog, setShowImportDialog] = useState(false);

  const { hasPermission } = usePermission();
  const [exporting, setExporting] = useState(false);
  const { warmupService } = useServices();
  const { mutateAsync: createBulkWarmups, isPending: isUploading } =
    useBulkCreateWarmups();

  const handleExportCsvWarmup = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      // Trigger client-side export using the Ag Grid instance on the page
      window.dispatchEvent(new Event("sender-profiles-export-csv"));
      window.cioanalytics?.track("sender_profiles_exported", {});
    } catch (error) {
      const errorMessage = extractErrorMessage(error, "Failed to export CSV");
      toast.error(errorMessage);
    } finally {
      setTimeout(() => setExporting(false), 800);
    }
  };

  const handleFileImport = async (file: File, onReset: () => void) => {
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const text = e.target?.result as string;
        const rows = text.split("\n");
        const headers = rows[0].toLowerCase();
        const emailIndex = headers
          .split(",")
          .findIndex((h) => h.includes("email"));

        if (emailIndex === -1) {
          toast.error("Invalid CSV format. Email column not found.");
          return;
        }

        const emails = rows
          .slice(1)
          .map((row) => row.split(",")[emailIndex]?.trim())
          .filter(
            (email) => email && validEmailSchema.safeParse(email).success
          );

        if (emails.length === 0) {
          toast.error("No valid emails found in the CSV file.");
          return;
        }

        await createBulkWarmups(file);

        toast.success("Successfully imported sender profiles");

        window.cioanalytics?.track("sender_profiles_imported", {
          count: emails.length,
          emails: emails,
        });
        onReset();
      };
      reader.readAsText(file);
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to import CSV file");
      toast.error(message);
      console.error("Import error:", error);
    }
  };

  const handleDownloadTemplate = () => {
    const template = "email\nuser@example.com";
    const blob = new Blob([template], { type: "text/csv" });
    saveAs(blob, "sender-profile-example-template.csv");
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className="w-full sm:w-auto" size="sm">
            <Upload className="h-4 w-4" />

            {hasPermission("mailboxes:add") ? "Import/Export" : "Export"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {hasPermission("mailboxes:add") && (
            <DropdownMenuItem onSelect={() => setShowImportDialog(true)}>
              <Upload className="h-4 w-4 shrink-0" />
              Import Senders
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={handleExportCsvWarmup} disabled={exporting}>
            {exporting ? (
              <Download className="h-4 w-4 shrink-0 animate-pulse" />
            ) : (
              <Download className="h-4 w-4 shrink-0" />
            )}
            {exporting ? "Exporting..." : "Export Senders"}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {hasPermission("mailboxes:add") && <AddSenderDialog />}
      {hasPermission("mailboxes:add") && <AddReminderDialog />}

      {/* Import Dialog */}
      <FileUploadDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        title="Import Senders"
        description="Upload a CSV file containing email addresses"
        accept=".csv"
        onFileSelect={handleFileImport}
        isUploading={isUploading}
        onDownloadTemplate={handleDownloadTemplate}
      />
    </>
  );
};

export default SenderProfileMainActions;

const MAX_REMINDER_EMAILS = 5;

const remindersSchema = z.object({
  emails: z
    .array(
      z.object({
        email: validEmailSchema,
      })
    )
    .min(1, "Add at least 1 email")
    .max(MAX_REMINDER_EMAILS, `You can add up to ${MAX_REMINDER_EMAILS} emails`),
  time: z.string().min(1, "Select time"),
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .refine(
      (value) => {
        // Normalize: remove dashes, spaces, and other non-digit characters except +
        const normalized = value.replace(/[^\d+]/g, '');
        // Validate: must start with + followed by country code and number
        return /^\+[1-9]\d{1,14}$/.test(normalized);
      },
      {
        message: "Phone number must start with + followed by country code (e.g., +91, +80)",
      }
    ),
});

type RemindersFormValues = z.infer<typeof remindersSchema>;

function AddReminderDialog() {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("add");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reminderToDelete, setReminderToDelete] = useState<string | null>(null);
  const { mutateAsync: createReminders, isPending } = useCreateSenderReminders();
  const { mutateAsync: deleteReminder, isPending: isDeleting } = useDeleteSenderReminders();

  const form = useForm<RemindersFormValues>({
    resolver: zodResolver(remindersSchema),
    defaultValues: {
      emails: [{ email: "" }],
      time: "",
      phoneNumber: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "emails",
  });

  // Call GET API when dialog opens to fetch all existing reminders
  const { data: remindersData, refetch: refetchReminders, isLoading: isLoadingReminders } = useGetSenderReminders(
    open // Only fetch when dialog is open
  );

  // Parse reminders data - it's an array of reminder objects
  const reminders = Array.isArray(remindersData) ? remindersData : [];

  // Get emails from form to check if they already have reminders
  const emailsWatch = form.watch("emails");
  const emailList = (emailsWatch || []).map((e) => e.email).filter(Boolean);

  // Check if any of the form emails already have reminders
  const existingEmails = reminders
    .filter((r: any) =>
      Array.isArray(r.emails) &&
      r.emails.some((email: string) => emailList.includes(email))
    )
    .flatMap((r: any) => r.emails || [])
    .filter((email: string) => emailList.includes(email));

  const times = Array.from({ length: 24 * 4 }, (_, i) => {
    const hour = Math.floor(i / 4);
    const minute = (i % 4) * 15;
    const hh = hour.toString().padStart(2, "0");
    const mm = minute.toString().padStart(2, "0");
    return `${hh}:${mm}`;
  });

  const handleSubmit = async (values: RemindersFormValues) => {
    try {
      // Validate time is present
      if (!values.time || values.time.trim() === "") {
        toast.error("Please select a time for the reminder");
        form.setError("time", { message: "Time is required" });
        return;
      }

      // Parse and validate time format
      const timeParts = values.time.split(":");
      if (timeParts.length !== 2) {
        toast.error("Invalid time format");
        form.setError("time", { message: "Invalid time format" });
        return;
      }

      const hh = parseInt(timeParts[0], 10);
      const mm = parseInt(timeParts[1], 10);

      if (isNaN(hh) || isNaN(mm) || hh < 0 || hh > 23 || mm < 0 || mm > 59) {
        toast.error("Invalid time values");
        form.setError("time", { message: "Invalid time values" });
        return;
      }

      // Create remindAt date
      const now = new Date();
      const remindAt = new Date(now);
      remindAt.setHours(hh);
      remindAt.setMinutes(mm);
      remindAt.setSeconds(0);
      remindAt.setMilliseconds(0);

      // If the time has passed today, set it for tomorrow
      if (remindAt <= now) {
        remindAt.setDate(remindAt.getDate() + 1);
      }

      const emails = values.emails.map((e) => e.email).filter(Boolean);

      if (emails.length === 0) {
        toast.error("Please add at least one email address");
        return;
      }

      if (existingEmails.length > 0) {
        toast.warning(
          `Reminder already set for: ${existingEmails.join(", ")}`
        );
      }

      // Ensure remindAt is a valid ISO string
      const remindAtISO = remindAt.toISOString();

      if (!remindAtISO) {
        toast.error("Failed to create reminder time");
        return;
      }

      // Normalize phone number: remove dashes and spaces before sending
      const normalizedPhoneNumber = values.phoneNumber.replace(/[^\d+]/g, '');

      await createReminders({
        emails,
        remindAt: remindAtISO,
        phoneNumber: normalizedPhoneNumber,
      });

      // Refetch reminders after creating new ones
      await refetchReminders();

      window.cioanalytics?.track("sender_reminders_created", {
        count: emails.length,
        emails,
        remindAt: remindAtISO,
      });

      setOpen(false);
      form.reset({
        emails: [{ email: "" }],
        time: "",
        phoneNumber: "",
      });
    } catch (error) {
      const message = extractErrorMessage(error, "Failed to create reminders");
      toast.error(message);
    }
  };

  // Reset tab when dialog opens/closes
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setActiveTab("add");
      refetchReminders();
    }
  };

  // Handle delete reminder
  const handleDeleteReminder = async () => {
    if (!reminderToDelete) return;
    try {
      await deleteReminder(reminderToDelete);
      await refetchReminders();
      setDeleteDialogOpen(false);
      setReminderToDelete(null);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  // Open delete confirmation dialog
  const openDeleteDialog = (id: string) => {
    setReminderToDelete(id);
    setDeleteDialogOpen(true);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="default" className="w-full sm:w-auto" size="sm" onClick={() => setOpen(true)}>
          <Bell className="h-4 w-4 mr-2" />
          Add Reminder
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Reminders</DialogTitle>
          <DialogDescription>
            Manage your email reminders for sender profiles
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="add">Add Reminder</TabsTrigger>
            <TabsTrigger value="view">
              View Reminders
              {reminders.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {reminders.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="add" className="flex-1 min-h-0 mt-4">
            <div className="mb-4">
              <p className="text-sm text-muted-foreground">
                Add up to {MAX_REMINDER_EMAILS} emails and select the daily reminder time.
              </p>
            </div>
            <Form {...form}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  form.handleSubmit(handleSubmit)(e);
                }}
                className="space-y-4"
              >
                <div className="max-h-[320px] overflow-y-auto space-y-3">
                  {fields.map((field, index) => (
                    <div key={field.id} className="flex items-start gap-3">
                      <FormField
                        control={form.control}
                        name={`emails.${index}.email`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormLabel className="text-xs">Email</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="user@domain.com"
                                type="email"
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.stopPropagation();
                                  }
                                }}
                              />
                            </FormControl>
                            <FormMessage className="font-normal" />
                          </FormItem>
                        )}
                      />
                      {fields.length > 1 && (
                        <Button
                          variant="ghost"
                          size="icon"
                          type="button"
                          onClick={() => remove(index)}
                          className="text-muted-foreground hover:text-foreground mt-6 h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    if (fields.length < MAX_REMINDER_EMAILS) append({ email: "" });
                  }}
                  className="w-full border-2 border-dashed border-muted-foreground/20 hover:border-primary/30 hover:bg-primary/5"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Add more
                </Button>

                <div className="grid grid-cols-1 sm:grid-cols-1 gap-3">
                  <FormField
                    control={form.control}
                    name="time"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel>Time</FormLabel>
                        <FormControl>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="w-full">
                              <div className="flex items-center">
                                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                                <SelectValue placeholder="Select time" />
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              <ScrollArea className="h-[200px]">
                                {times.map((time) => {
                                  const [h, m] = time.split(":").map(Number);
                                  const period = h >= 12 ? "PM" : "AM";
                                  const displayHour = h % 12 === 0 ? 12 : h % 12;
                                  return (
                                    <SelectItem key={time} value={time}>
                                      {`${displayHour}:${m.toString().padStart(2, "0")} ${period}`}
                                    </SelectItem>
                                  );
                                })}
                              </ScrollArea>
                            </SelectContent>
                          </Select>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <FormLabel className="flex items-center gap-2">
                          Phone Number
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Phone className="h-3 w-3 text-muted-foreground cursor-help" />
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Required. Should be a WhatsApp number with country code extension (e.g., +91, +80)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            placeholder="+91-9876543210"
                            inputMode="tel"
                            onChange={(e) => {
                              let value = e.target.value;

                              // Remove invalid characters (allow digits, +, -)
                              value = value.replace(/[^0-9+-]/g, '');

                              // Allow + only at the beginning
                              if (value.includes('+')) {
                                value =
                                  (value.startsWith('+') ? '+' : '') +
                                  value.replace(/\+/g, '');
                              }

                              // Count digits only
                              const digits = value.replace(/[^0-9]/g, '');

                              // Enforce digit length (8–11)
                              if (digits.length >= 8 && digits.length <= 12 || digits.length < 8) {
                                field.onChange(value);
                              }
                            }}
                            onKeyDown={(e) => {
                              const allowedKeys = [
                                'Backspace',
                                'Delete',
                                'ArrowLeft',
                                'ArrowRight',
                                'Tab',
                                'Enter',
                              ];

                              // Allow digits, +, -
                              if (
                                !allowedKeys.includes(e.key) &&
                                !/^[0-9+-]$/.test(e.key)
                              ) {
                                e.preventDefault();
                              }

                              // Allow + only at start
                              if (e.key === '+' && field.value?.length > 0) {
                                e.preventDefault();
                              }

                              // Prevent form submit
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.stopPropagation();
                              }
                            }}
                          />
                        </FormControl>


                        <FormMessage className="font-normal" />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setOpen(false);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        Saving...
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </form>
            </Form>
          </TabsContent>

          <TabsContent value="view" className="flex-1 min-h-0 mt-4">
            <div className="space-y-4">
              {isLoadingReminders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : reminders.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="text-sm">No reminders set yet</p>
                  <p className="text-xs mt-1">Add a reminder using the "Add Reminder" tab</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px] pr-4">
                  <div className="space-y-3">
                    {reminders.map((reminder: any) => {
                      const reminderDate = new Date(reminder.date);
                      const formattedDate = format(reminderDate, "MMM dd, yyyy");
                      const formattedTime = format(reminderDate, "h:mm a");

                      return (
                        <div
                          key={reminder._id}
                          className="rounded-lg border border-border bg-card p-4 space-y-3"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Clock className="h-4 w-4 text-muted-foreground" />
                                <span className="text-sm font-medium">
                                  Daily at {formattedTime}
                                </span>
                                <Badge
                                  variant={reminder.isSent ? "default" : "secondary"}
                                  className="ml-2"
                                >
                                  {reminder.isSent ? "Sent today" : "Pending today"}
                                </Badge>
                              </div>
                              <div className="space-y-1">
                                <p className="text-xs text-muted-foreground">Emails:</p>
                                <div className="flex flex-wrap gap-2">
                                  {Array.isArray(reminder.emails) &&
                                    reminder.emails.map((email: string, idx: number) => (
                                      <Badge
                                        key={idx}
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {email}
                                      </Badge>
                                    ))}
                                </div>
                              </div>
                              {reminder.phoneNumber && (
                                <div className="space-y-1 mt-2">
                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Phone className="h-3 w-3" />
                                    WhatsApp:
                                  </p>
                                  <Badge variant="outline" className="text-xs">
                                    {reminder.phoneNumber}
                                  </Badge>
                                </div>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openDeleteDialog(reminder._id)}
                              disabled={isDeleting}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                          <div className="text-xs text-muted-foreground pt-2 border-t border-border">
                            Created: {format(new Date(reminder.createdAt), "MMM dd, yyyy 'at' h:mm a")}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Reminder</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this reminder? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setReminderToDelete(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteReminder}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
